/**
 * API Service Layer for Cloudflare D1
 * Handles all communication with the Worker API
 */

const getApiBase = () => {
  // Use environment variable or default to empty string (will be replaced at build time)
  return 'https://wedding-rsvp-api.shaoshii.workers.dev';
};

const fetchAPI = async (endpoint, options = {}) => {
  const apiBase = getApiBase();
  const url = `${apiBase}${endpoint}`;
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(errorData.error || `HTTP ${response.status}: Request failed`);
  }

  return response.json();
};

/**
 * Look up a guest by first and last name
 * @param {string} firstName - Guest's first name
 * @param {string} lastName - Guest's last name
 * @returns {Promise<Array>} Array of matching guests
 */
export async function lookupGuest(firstName, lastName) {
  const encodedFirst = encodeURIComponent(firstName.trim());
  const encodedLast = encodeURIComponent(lastName.trim());
  
  return fetchAPI(`/api/guest?firstName=${encodedFirst}&lastName=${encodedLast}`);
}

/**
 * Submit an RSVP response
 * @param {Object} formData - RSVP form data
 * @returns {Promise<Object>} Response with success status and ID
 */
export async function submitRSVP(formData) {
  return fetchAPI('/api/rsvp', {
    method: 'POST',
    body: JSON.stringify(formData),
  });
}

/**
 * Get all RSVPs (admin function)
 * @param {string} passcode - Admin passcode
 * @returns {Promise<Array>} Array of RSVPs
 */
export async function getRSVPs(passcode) {
  const encodedPasscode = encodeURIComponent(passcode);
  return fetchAPI(`/api/admin/rsvps?passcode=${encodedPasscode}`);
}

/**
 * Get guest list (admin function)
 * @param {string} passcode - Admin passcode
 * @returns {Promise<Array>} Array of guests
 */
export async function getGuestList(passcode) {
  const encodedPasscode = encodeURIComponent(passcode);
  return fetchAPI(`/api/admin/guests?passcode=${encodedPasscode}`);
}

/**
 * Convert camelCase to snake_case for API requests
 * @param {Object} obj - Object with camelCase properties
 * @returns {Object} Object with snake_case properties
 */
function toSnakeCase(obj) {
  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Convert camelCase to snake_case
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      result[snakeKey] = obj[key];
    }
  }
  return result;
}

/**
 * Add a new guest to the invitee list (admin function)
 * @param {Object} guestData - Guest information
 * @param {string} passcode - Admin passcode
 * @returns {Promise<Object>} Response with success status and ID
 */
export async function addGuest(guestData, passcode) {
  const encodedPasscode = encodeURIComponent(passcode);
  // Convert camelCase to snake_case for API
  const snakeCaseData = toSnakeCase(guestData);
  return fetchAPI(`/api/admin/guest?passcode=${encodedPasscode}`, {
    method: 'POST',
    body: JSON.stringify(snakeCaseData),
  });
}

/**
 * Delete an RSVP (admin function)
 * @param {number} rsvpId - RSVP ID to delete
 * @param {string} passcode - Admin passcode
 * @returns {Promise<Object>} Response with success status
 */
export async function deleteRSVP(rsvpId, passcode) {
  const encodedPasscode = encodeURIComponent(passcode);
  return fetchAPI(`/api/admin/rsvp/${rsvpId}?passcode=${encodedPasscode}`, {
    method: 'DELETE',
  });
}

/**
 * Delete a guest from the invitee list (admin function)
 * @param {number} guestId - Guest ID to delete
 * @param {string} passcode - Admin passcode
 * @returns {Promise<Object>} Response with success status
 */
export async function deleteGuest(guestId, passcode) {
  const encodedPasscode = encodeURIComponent(passcode);
  return fetchAPI(`/api/admin/guest/${guestId}?passcode=${encodedPasscode}`, {
    method: 'DELETE',
  });
}
